/**
 * Fair dealing helpers: shuffle, max-2-per-tier hands, prefer one card per
 * tier (1–4), and top-ups that reshuffle the whole row.
 */

export const MAX_PER_TIER = 2;
export const HAND_SIZE = 5;
export const TOP_UP_COUNT = 3;
export const REQUIRED_TIERS = [1, 2, 3, 4];

export function shuffle(array) {
  const result = Array.isArray(array) ? [...array] : [];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function tierOf(card) {
  const n = Number(card?.ranking);
  return Number.isFinite(n) ? n : 0;
}

function idOf(card) {
  return card?.id;
}

function tierCounts(hand) {
  const counts = new Map();
  for (const card of hand) {
    const t = tierOf(card);
    counts.set(t, (counts.get(t) || 0) + 1);
  }
  return counts;
}

function tiersPresentInPool(pool) {
  const present = new Set();
  for (const card of pool || []) {
    const t = tierOf(card);
    if (REQUIRED_TIERS.includes(t)) present.add(t);
  }
  return REQUIRED_TIERS.filter((t) => present.has(t));
}

function coverageMeta(hand, required) {
  const achieved = required.filter((t) => (tierCounts(hand).get(t) || 0) > 0);
  const missing = required.filter((t) => !achieved.includes(t));
  return { required: [...required], achieved, missing };
}

/**
 * Pick one card of `tier` from candidates, optionally respecting exclude set.
 */
function pickForTier(candidates, used, tier, excludeSet, respectExclude) {
  for (const card of candidates) {
    const id = idOf(card);
    if (used.has(id)) continue;
    if (respectExclude && excludeSet.has(id)) continue;
    if (tierOf(card) !== tier) continue;
    return card;
  }
  return null;
}

/**
 * Build a hand preferring one card from each available required tier,
 * then fill remaining slots under maxPerTier. Relaxes exclude then the
 * tier cap if the hand cannot otherwise be filled.
 *
 * @returns {{ hand: object[], remaining: object[], meta: object }}
 */
export function buildHand(pool, count = HAND_SIZE, options = {}) {
  const maxPerTier = options.maxPerTier ?? MAX_PER_TIER;
  const excludeSet = new Set(options.excludeIds || []);

  const source = shuffle(pool || []);
  const prefer = [];
  const fallback = [];

  for (const card of source) {
    if (excludeSet.has(idOf(card))) fallback.push(card);
    else prefer.push(card);
  }

  const required = tiersPresentInPool(source);
  const hand = [];
  const used = new Set();
  let usedExclude = false;
  let relaxedTier = false;

  const take = (card) => {
    if (!card) return false;
    const id = idOf(card);
    if (used.has(id)) return false;
    hand.push(card);
    used.add(id);
    if (excludeSet.has(id)) usedExclude = true;
    return true;
  };

  // Phase A: coverage — one card per required tier
  for (const tier of required) {
    if (hand.length >= count) break;
    let card = pickForTier(prefer, used, tier, excludeSet, true);
    if (!card) card = pickForTier(fallback, used, tier, excludeSet, false);
    take(card);
  }

  const tryPick = (candidates, { respectExclude, respectTier }) => {
    for (const card of candidates) {
      if (hand.length >= count) break;
      const id = idOf(card);
      if (used.has(id)) continue;
      if (respectExclude && excludeSet.has(id)) continue;

      const counts = tierCounts(hand);
      const t = tierOf(card);
      if (respectTier && (counts.get(t) || 0) >= maxPerTier) continue;

      take(card);
    }
  };

  // Phase B: fill remaining slots (5th card, etc.) under max 2/tier
  tryPick(prefer, { respectExclude: true, respectTier: true });
  if (hand.length < count) {
    tryPick(fallback, { respectExclude: false, respectTier: true });
  }
  if (hand.length < count) {
    relaxedTier = true;
    tryPick([...prefer, ...fallback], { respectExclude: false, respectTier: false });
  }

  const remaining = source.filter((c) => !used.has(idOf(c)));
  const tierCoverage = coverageMeta(hand, required);

  return {
    hand: shuffle(hand),
    remaining,
    meta: {
      usedExclude,
      relaxedTier,
      excludedAvailable: fallback.length,
      preferredAvailable: prefer.length,
      tierCoverage,
    },
  };
}

/**
 * Order the draw pile so recently-seen cards come last (unseen-first).
 */
export function orderDrawPile(remaining, excludeIds = []) {
  const excludeSet = new Set(excludeIds);
  const unseen = [];
  const seen = [];
  for (const card of shuffle(remaining || [])) {
    if (excludeSet.has(idOf(card))) seen.push(card);
    else unseen.push(card);
  }
  return [...unseen, ...seen];
}

function missingTiers(hand, required) {
  const counts = tierCounts(hand);
  return required.filter((t) => (counts.get(t) || 0) === 0);
}

/**
 * Pull `need` new cards for a top-up against survivors, preferring tiers
 * still missing from the table, then shuffle the full hand.
 *
 * @returns {{ newCards: object[], hand: object[], poolAfter: object[], meta: object }}
 */
export function buildTopUp(remaining, pool, need = TOP_UP_COUNT, options = {}) {
  const maxPerTier = options.maxPerTier ?? MAX_PER_TIER;
  const survivors = Array.isArray(remaining) ? [...remaining] : [];
  const working = Array.isArray(pool) ? [...pool] : [];
  const required = tiersPresentInPool([...survivors, ...working]);

  const newCards = [];
  let relaxedTier = false;
  const missingBefore = missingTiers(survivors, required);

  const canTake = (card, respectTier) => {
    const counts = tierCounts([...survivors, ...newCards]);
    const t = tierOf(card);
    if (respectTier && (counts.get(t) || 0) >= maxPerTier) return false;
    return true;
  };

  const takeAt = (index) => {
    const card = working[index];
    newCards.push(card);
    working.splice(index, 1);
  };

  /** Prefer a card whose tier is still missing from survivors + newCards. */
  const takeMissing = (respectTier) => {
    while (newCards.length < need) {
      const missing = missingTiers([...survivors, ...newCards], required);
      if (!missing.length) break;

      let found = -1;
      for (let i = 0; i < working.length; i++) {
        const card = working[i];
        if (!missing.includes(tierOf(card))) continue;
        if (!canTake(card, respectTier)) continue;
        found = i;
        break;
      }
      if (found === -1) break;
      takeAt(found);
    }
  };

  const takeAny = (respectTier) => {
    let i = 0;
    while (i < working.length && newCards.length < need) {
      const card = working[i];
      if (!canTake(card, respectTier)) {
        i += 1;
        continue;
      }
      takeAt(i);
    }
  };

  // Prefer filling missing tiers, then any legal under max 2, then relax
  takeMissing(true);
  takeAny(true);
  if (newCards.length < need) {
    relaxedTier = true;
    takeMissing(false);
    takeAny(false);
  }

  const hand = shuffle([...survivors, ...newCards]);
  const missingAfter = missingTiers(hand, required);

  return {
    newCards,
    hand,
    poolAfter: working,
    meta: {
      relaxedTier,
      survivorTiers: survivors.map(tierOf),
      newTiers: newCards.map(tierOf),
      missingBefore,
      missingAfter,
      tierCoverage: coverageMeta(hand, required),
    },
  };
}

/** Compact rows for console.table debugging. */
export function dealLogRows(cards) {
  return (cards || []).map((c, index) => ({
    slot: index + 1,
    id: c?.id,
    title: c?.title || '',
    tier: tierOf(c),
  }));
}
